import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import type { SxProps, Theme } from "@mui/material";
import {
  Box,
  Button,
  DialogActions,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { linearProgressClasses } from "@mui/material/LinearProgress";
import { useCallback, useMemo, useState } from "react";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import type { Dashboard } from "../../../../api/types/dashboardsTypes";
import { ValidationInfoItem } from "../../reporting/report-lists/reports/copyto/CompanyMultipleReportsListItemContainer";
import { InProgressIcon } from "../../reporting/report-lists/reports/copyto/controls/InProgressIcon";
import type { TabNames } from "../../reporting/report-lists/reports/copyto/controls/ResultButtonGroup";
import ResultButtonGroup from "../../reporting/report-lists/reports/copyto/controls/ResultButtonGroup";
import { useCopyToEligibleCompanies } from "../../reporting/report-lists/reports/hooks/useCopyToEligibleCompanies";
import type { SelectedCompany } from "../hooks/validationStateSlice";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import { getDashboardViewUrl } from "../utils/dashboardUrls";
import CancelPromptDialog from "./CancelPromptDialog";
import { CompanyDashboardListItem } from "./components";
import CompanyContent from "./components/CompanyContent";
import CompanyHeader from "./components/CompanyHeader";
import { TileItemCopying } from "./components/copying-step/TileItemCopying";
import type { CopyCompany, CopyCompanyDashboard } from "./hooks/CopyCompany";
import useDashboardCopying from "./hooks/useDashboardCopying";

interface Props {
  dashboards: Dashboard[];
  selectedCompanies: SelectedCompany[];
  copyConditionValues: boolean;
  onClose: (success?: boolean) => void;
}
export function CopyToDialogCopying({ dashboards, selectedCompanies, copyConditionValues, onClose }: Props) {
  const { copying, retryCopy, cancel } = useDashboardCopying(selectedCompanies, copyConditionValues);
  const [selectedTab, setSelectedTab] = useState<TabNames>("all");
  const [showPrompt, setShowPrompt] = useState(false);

  const allCopyingDashboards = useMemo(() => copying.flatMap((c) => c.dashboards), [copying]);

  const copiedToAllCompanies = useMemo(
    () => allCopyingDashboards.every((r) => r.copied || r.error),
    [allCopyingDashboards]
  );
  const copiedWithErrors = useMemo(() => allCopyingDashboards.some((r) => r.error), [allCopyingDashboards]);
  const copied = useMemo(() => allCopyingDashboards.filter((r) => r.copied).length, [allCopyingDashboards]);
  const failed = useMemo(() => allCopyingDashboards.filter((r) => r.error).length, [allCopyingDashboards]);
  const progressValue = useMemo(() => {
    const value = (copied / allCopyingDashboards.length) * 100;
    return Number.isNaN(value) ? 0 : value;
  }, [allCopyingDashboards.length, copied]);

  const filteredCompanyList = useMemo(() => {
    switch (selectedTab) {
      case "all":
        return copying;
      case "copied":
        return copying
          .filter((c) => c.dashboards.some((r) => r.copied))
          .map((c) => ({ ...c, dashboards: c.dashboards.filter((r) => r.copied) }));
      case "failed":
        return copying
          .filter((c) => c.dashboards.some((r) => r.error))
          .map((c) => ({ ...c, dashboards: c.dashboards.filter((r) => r.error) }));
    }
  }, [copying, selectedTab]);

  const handleClosing = useCallback(() => {
    if (copiedToAllCompanies) {
      onClose(copiedToAllCompanies);
    } else {
      setShowPrompt(true);
    }
  }, [copiedToAllCompanies, onClose]);

  return (
    <>
      <DialogHeader text={pluralize("Copy Dashboard", dashboards.length)} onClose={handleClosing} />
      <Divider />
      <Stack sx={{ flex: 1, width: "100%" }}>
        <Grid container sx={{ flex: 1, justifyContent: "center", p: 2, width: "100%" }}>
          <Grid container sx={{ gap: 1, width: "55%" }}>
            <Stack sx={{ width: "100%" }}>
              <Typography color="secondary">Do not close this window until the copying process is complete</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", pt: 0.5 }}>
                <BorderLinearProgress variant="determinate" value={progressValue} sx={{ flex: 1 }} />
                <Typography color="secondary">
                  {copied}/{dashboards.length}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ gap: 1, height: 20 }}>
                {copiedToAllCompanies && (
                  <Typography color="secondary">
                    {copiedWithErrors ? "Completion with errors occurred" : "Completed!"}
                  </Typography>
                )}
              </Stack>
              <Box sx={{ pt: 2.5, width: "50%" }}>
                <ResultButtonGroup
                  copied={copied}
                  failed={failed}
                  total={allCopyingDashboards.length}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
              </Box>
              <ScrollableFlexContainer>
                <DashboardCopying companies={filteredCompanyList} retryCopy={retryCopy} />
              </ScrollableFlexContainer>
            </Stack>
          </Grid>
        </Grid>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="contained" onClick={handleClosing}>
            Close
          </Button>
        </DialogActions>
      </Stack>
      {showPrompt && (
        <CancelPromptDialog
          dashboards={dashboards}
          onOkay={() => {
            cancel();
            onClose();
          }}
          onCancel={() => setShowPrompt(false)}
        />
      )}
    </>
  );
}

interface DashboardCopyingProps {
  companies: CopyCompany[];
  retryCopy: (dashboard: CopyCompanyDashboard) => void;
}

function DashboardCopying({ companies, retryCopy }: DashboardCopyingProps) {
  const eligibleCompanies = useCopyToEligibleCompanies(["ManageReports", "ManageDashboards"]);

  const companyList = useMemo(() => {
    return eligibleCompanies
      .filter((c) => c.type === "FundManager")
      .map((c): ListItemData => ({
        value: c.clientCode,
        label: c.title,
        iconSrc: c.branding.logoMarkUrl,
      }));
  }, [eligibleCompanies]);

  const getAction = useCallback(
    (dashboard: CopyCompanyDashboard) => {
      if (dashboard.copied) {
        return <OpenDashboard sx={{ top: 0 }} companyDashboard={dashboard} />;
      }
      if (dashboard.error) {
        return <TryCopyAgain sx={{ top: 0 }} onRetry={() => retryCopy(dashboard)} />;
      }
      return undefined;
    },
    [retryCopy]
  );

  return (
    <Stack sx={{ gap: 1, pt: 2, pb: 1 }}>
      {companies.map((company) => {
        const isCopying = company.dashboards.some((r) => r.copying);

        return (
          <Paper
            key={company.company.code}
            variant="outlined"
            sx={(theme) => ({ borderColor: theme.palette.divider, width: "100%" })}
          >
            <Stack>
              <CompanyHeader companyBranding={companyList.find((c) => c.value === company.company.code)} />

              <CompanyContent
                company={company.company}
                summary={isCopying ? <CopyingInfo company={company} /> : <CopiedInfo company={company} />}
              >
                {company.dashboards.map((dashboard) => {
                  const isCopying = dashboard.copying;
                  const isError = dashboard.error;
                  return (
                    <CompanyDashboardListItem
                      key={dashboard.dashboard.dashboardId}
                      dashboard={dashboard.dashboard}
                      widgets={dashboard.tiles.length}
                      Summary={
                        isError || dashboard.copied ? undefined : isCopying ? (
                          <CopyingInfo company={company} />
                        ) : (
                          <CopiedInfo company={company} />
                        )
                      }
                      Action={isCopying ? undefined : getAction(dashboard)}
                    >
                      {dashboard.tiles.map((tile) => (
                        <TileItemCopying key={tile.tile.tile.tileId} tile={tile} dashboard={dashboard} />
                      ))}
                    </CompanyDashboardListItem>
                  );
                })}
              </CompanyContent>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function OpenDashboard({ companyDashboard, sx }: { companyDashboard: CopyCompanyDashboard; sx?: SxProps<Theme> }) {
  const link = useMemo(() => {
    return getDashboardViewUrl(companyDashboard.toCompanyCode, companyDashboard.newDashboardId || "");
  }, [companyDashboard.toCompanyCode, companyDashboard.newDashboardId]);

  return (
    <Box sx={{ position: "relative" }}>
      <Link
        href={link}
        target="_blank"
        sx={{
          whiteSpace: "nowrap",
          textDecoration: "none",
          position: "absolute",
          right: (theme) => theme.spacing(1),
          top: (theme) => theme.spacing(1.2),
          ...sx,
        }}
      >
        <Typography variant="subtitle2">Open Dashboard</Typography>
      </Link>
    </Box>
  );
}

function TryCopyAgain({ onRetry, sx }: { onRetry?: () => void; sx?: SxProps<Theme> }) {
  return (
    <Box sx={{ position: "relative" }}>
      <Button
        color="error"
        sx={{
          whiteSpace: "nowrap",
          position: "absolute",
          right: (theme) => theme.spacing(1),
          top: (theme) => theme.spacing(0.5),
          ...sx,
        }}
        onClick={onRetry}
      >
        Try Again
      </Button>
    </Box>
  );
}

function CopyingInfo({ company }: { company: CopyCompany }) {
  const validatedCount = useMemo(() => {
    return company.dashboards.filter((r) => r.copied || r.error).length;
  }, [company.dashboards]);

  return (
    <Stack direction={"row"} spacing={1}>
      <Typography color={"secondary"}>
        {validatedCount}/{company.dashboards.length}
      </Typography>
      <InProgressIcon />
    </Stack>
  );
}

function CopiedInfo({ company }: { company: CopyCompany }) {
  const failedCount = useMemo(() => {
    return company.dashboards.filter((r) => r.error).length;
  }, [company.dashboards]);

  const successCount = useMemo(() => {
    return company.dashboards.filter((r) => r.copied && !r.error).length;
  }, [company.dashboards]);

  return (
    <Stack direction="row" spacing={1.5}>
      <ValidationInfoItem count={failedCount} Icon={ErrorRoundedIcon} color="error" />
      <ValidationInfoItem count={successCount} Icon={CheckRoundedIcon} color="success" />
    </Stack>
  );
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 4,
  },
}));
